import React, { useEffect } from 'react'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { FormValues } from './initialValues'
import {
  Beneficiary,
  Contract,
  ContractTenant,
  ResidentAppLocale,
  UserMetadata,
} from '../../../../common/validations'
import i18n from '../../../i18n/i18n'
import { ContractTerminationRequestPreview } from '../Confirm/ContractTerminationRequestPreview'
import { formatFormValues } from '../utils/formatFormValues'

import { useAPIConfirmCreateTermination } from 'api/apiHooks/useAPIConfirmCreateTermination'

interface TerminateContractPreviewProps {
  formValues: FormValues
  contract: Contract
  cancel: () => void
  tenants: ContractTenant[]
  userMetadata: UserMetadata
  initialBeneficiaries: Beneficiary[]
  setIsConfirming: (isConfirming: boolean) => void
}

export const TerminateContractPreview: React.FC<
  TerminateContractPreviewProps
> = ({
  formValues,
  cancel,
  tenants,
  contract,
  userMetadata: { isImpersonator, identityProvider },
  initialBeneficiaries,
  setIsConfirming,
}) => {
  const { t } = useTranslation(['common', 'terminateContract'])

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } = useAPIConfirmCreateTermination(
    contract.uuid,
    lang
  )

  useEffect(() => {
    if (isSuccess && data && data.link) {
      window.location.href = data.link
    }
  })

  useEffect(
    () => setIsConfirming(isPending || isSuccess),
    [isPending, isSuccess, setIsConfirming]
  )

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    setIsConfirming(true)
    mutate({
      operationData: formatFormValues(formValues, true, initialBeneficiaries),
      provider: identityProvider,
    })
  }

  // We want to show the loading spinner until the user is redirected
  const isLoading = isPending || isSuccess

  return (
    <>
      <ContractTerminationRequestPreview
        contractTerminationRequest={{
          ...formValues,
          beneficiaries: formValues.beneficiaries.map((b) => {
            return {
              ...b,
              returnPercentage: b.returnPercentage,
              rooReturnPercentage: b.rooReturnPercentage,
            }
          }),
        }}
        tenants={tenants}
        contract={contract}
        confirmButtonLabel={t('terminateContract:buttons.confirmTermination')}
        backToEditButtonLabel={t('terminateContract:buttons.backToEdit')}
      />
      <div className="min-tablet:mt-16 min-tablet:flex min-tablet:items-center min-tablet:justify-end flex gap-4">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('terminateContract:buttons.backToEdit')}
        </Button>
        <Button
          type="button"
          variant="delete"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isLoading}
          loading={isLoading}
        >
          {t('terminateContract:buttons.confirmTermination')}
        </Button>
      </div>
    </>
  )
}
